import React from 'react';
import { Badge, type BadgeProps } from '@/design-system/badge';
import {
  BATCH_STATUS_LABELS,
  BatchStatus,
  DISPATCH_STATUS_LABELS,
  DispatchItemStatus,
  DispatchStatus,
  StopKind,
  tripKindOf,
} from '@/services/api/dispatches';
import {
  ITEM_CONDITION_LABELS,
  ITEM_STATUS_LABELS as SHARED_ITEM_STATUS_LABELS,
  type ItemCondition,
} from '@/services/api/status';

type BadgeStatus = NonNullable<BadgeProps['status']>;
type BadgeSize = NonNullable<BadgeProps['size']>;

/**
 * Tones map onto the operational meaning of each run, mirroring the
 * ShipmentBadges palette decisions:
 *   PREVIEW   → information (persisted plan, not yet committed)
 *   COMMITTED → feature     (brand-live; bridged to DS, items in flight)
 *   PARTIAL   → away        (amber — some batches failed; needs attention)
 *   FAILED    → error       (none bridged; rerun)
 *   CANCELLED → neutral     (run cancelled before commit)
 */
export const DISPATCH_STATUS_TONE: Record<DispatchStatus, BadgeStatus> = {
  PREVIEW: 'information',
  COMMITTED: 'feature',
  PARTIAL: 'away',
  FAILED: 'error',
  CANCELLED: 'neutral',
};

export const DispatchStatusBadge: React.FC<{ status: DispatchStatus }> = ({
  status,
}) => (
  <Badge status={DISPATCH_STATUS_TONE[status]} type="dot">
    {DISPATCH_STATUS_LABELS[status]}
  </Badge>
);

/**
 * Sentinel `triggered_by` written by the scheduled pitstop auto-dispatch
 * (client-pitstop pickup TRD §D6) — every other value is a human actor.
 */
export const SYSTEM_PITSTOP_SCHEDULE_TRIGGER = 'SYSTEM_PITSTOP_SCHEDULE';

/**
 * "Auto" chip for dispatches created by the pitstop schedule tick instead of
 * an ops user. Renders nothing for human-triggered dispatches.
 */
export const AutoDispatchBadge: React.FC<{
  triggeredBy?: string | null;
  size?: BadgeSize;
}> = ({ triggeredBy, size = 'md' }) =>
  triggeredBy === SYSTEM_PITSTOP_SCHEDULE_TRIGGER ? (
    // Warning (yellow) on purpose: an automated actor touched shipments —
    // ops should notice it at a glance among the human-triggered rows.
    <Badge
      status="warning"
      size={size}
      type="left-icon"
      icon={<i className="ri-timer-flash-line" aria-hidden="true" />}
    >
      Auto
    </Badge>
  ) : null;

/**
 * Batch tones inside a dispatch's detail tree.
 *   PLANNED    → information (will ship at commit)
 *   DEFERRED   → neutral     (skipped this run; items stay HUB_SCANNED)
 *   DISPATCHED → feature      (bridged to DS)
 *   FAILED     → error       (bridge error)
 */
export const BATCH_STATUS_TONE: Record<BatchStatus, BadgeStatus> = {
  PLANNED: 'information',
  DEFERRED: 'neutral',
  DISPATCHED: 'feature',
  FAILED: 'error',
};

export const BatchStatusBadge: React.FC<{ status: BatchStatus }> = ({
  status,
}) => (
  <Badge status={BATCH_STATUS_TONE[status]} type="dot">
    {BATCH_STATUS_LABELS[status]}
  </Badge>
);

/**
 * Live per-item status (§D9). Shown on every item row in the dispatch detail
 * so the plan reads honestly — including items the BE has already excluded
 * from the counts (CANCELLED / LOST). `HUB_SCANNED` is the normal "ready to
 * ship" state and renders no badge to keep the list quiet. Damaged parcels are
 * *not* excluded any more: damage is `condition` and they still ship.
 */
// Labels + tones mirror the Inbound page (src/pages/inbound) so an item reads
// the same everywhere, with dispatch-local wording for the pre-scan state.
// CANCELLED isn't an Inbound tab — kept here for the dispatch cancel flow.
export const ITEM_STATUS_LABELS: Partial<Record<DispatchItemStatus, string>> = {
  ...SHARED_ITEM_STATUS_LABELS,
  CREATED: 'Belum scan',
  HUB_SCANNED: 'Scanned',
};

export const ITEM_STATUS_TONE: Partial<Record<DispatchItemStatus, BadgeStatus>> =
  {
    CREATED: 'away',
    HUB_SCANNED: 'success',
    // Committed into a dispatch — the state this screen produces.
    DISPATCHED: 'feature',
    HANDOVER_SCANNED: 'verified',
    IN_DELIVERY: 'information',
    COMPLETED: 'success',
    IN_RETURN: 'warning',
    RETURNED_TO_HUB: 'error',
    RETURNED_TO_CLIENT: 'error',
    LOST: 'error',
    CANCELLED: 'neutral',
    // Legacy (pre-migration rows)
    DELIVERED: 'success',
    RETURNED: 'error',
    DAMAGED: 'away',
  };

export const ItemStatusBadge: React.FC<{
  status: DispatchItemStatus;
  size?: BadgeSize;
  /** Hide HUB_SCANNED (the normal ready state). Default false — show every
   *  status, matching the Inbound page. */
  hideReady?: boolean;
  /**
   * Goods the rider collects (they never see a hub scan). `CREATED` is their
   * ready state, so it reads "Siap pickup" instead of the "Belum scan" warning
   * — the same lens the BE commit gate uses.
   */
  skipsHubScan?: boolean;
}> = ({ status, size = 'sm', hideReady = false, skipsHubScan = false }) => {
  if (hideReady && status === 'HUB_SCANNED') return null;
  if (status === 'CREATED' && skipsHubScan) {
    return (
      <Badge status="verified" size={size}>
        Siap pickup
      </Badge>
    );
  }
  return (
    <Badge status={ITEM_STATUS_TONE[status] ?? 'neutral'} size={size}>
      {ITEM_STATUS_LABELS[status] ?? status}
    </Badge>
  );
};

/**
 * Which way a trip runs relative to the hub, from its stops' kinds:
 *   - every stop a dropoff → "Trip dropoff" (departs the hub/pitstop loaded)
 *   - every stop a pickup  → "Trip pickup" (ends at the hub/pitstop loaded)
 *   - both (only via a manual merge/move — the planner never mixes) →
 *     "Campur · hub → hub", flagged so ops notices the round trip.
 * Nothing for an empty batch.
 */
export const TripKindBadge: React.FC<{
  stops: ReadonlyArray<{ kind?: StopKind }>;
  size?: BadgeSize;
}> = ({ stops, size = 'sm' }) => {
  const kind = tripKindOf(stops);
  if (kind === null) return null;
  if (kind === 'DROPOFF') {
    return (
      <Badge
        status="information"
        size={size}
        type="left-icon"
        icon={<i className="ri-inbox-archive-line" aria-hidden="true" />}
      >
        Trip dropoff
      </Badge>
    );
  }
  if (kind === 'PICKUP') {
    return (
      <Badge
        status="verified"
        size={size}
        type="left-icon"
        icon={<i className="ri-inbox-unarchive-line" aria-hidden="true" />}
      >
        Trip pickup
      </Badge>
    );
  }
  return (
    <Badge status="warning" size={size}>
      Trip campur
    </Badge>
  );
};

/**
 * What the rider does at a stop, always shown: "Pickup" (collects — a
 * retrieval shipment's origin) or "Dropoff" (hands over). An absent `kind`
 * (older BE) reads as Dropoff.
 */
export const StopKindBadge: React.FC<{
  kind?: StopKind;
  size?: BadgeSize;
}> = ({ kind, size = 'sm' }) =>
  kind === 'PICKUP' ? (
    <Badge
      status="verified"
      size={size}
      type="left-icon"
      icon={<i className="ri-inbox-unarchive-line" aria-hidden="true" />}
    >
      Pickup
    </Badge>
  ) : (
    <Badge
      status="information"
      size={size}
      type="left-icon"
      icon={<i className="ri-inbox-archive-line" aria-hidden="true" />}
    >
      Dropoff
    </Badge>
  );

/**
 * Damage flag (v2 `condition`) — shown next to the status badge, since damage
 * is no longer a status and a damaged parcel still ships normally.
 */
export const ItemConditionBadge: React.FC<{
  condition?: ItemCondition | null;
  size?: BadgeSize;
}> = ({ condition, size = 'sm' }) =>
  condition === 'DAMAGED' ? (
    <Badge status="away" size={size}>
      {ITEM_CONDITION_LABELS.DAMAGED}
    </Badge>
  ) : null;

/**
 * Scan-state warning (§D9) — "N belum scan" on rider groups, batches, and
 * stops. Amber = operational aging tone, not generic warning.
 */
export const UnscannedBadge: React.FC<{
  count?: number;
  size?: BadgeSize;
}> = ({ count, size = 'md' }) =>
  count && count > 0 ? (
    <Badge
      status="away"
      size={size}
      type="left-icon"
      className="tabular-nums"
      icon={<i className="ri-alert-line" aria-hidden="true" />}
    >
      {count} belum scan
    </Badge>
  ) : null;
